import React, { useEffect, useState, useCallback } from "react";
import { useParams, useNavigate, useLocation } from "react-router-dom";
import {
  Container,
  Typography,
  CircularProgress,
  Alert,
  Box,
  IconButton,
  Grid,
  Button,
  useMediaQuery,
  useTheme,
} from "@mui/material";
import { fetchBlogs } from "../Api/blogFetch";
import ShareIcon from "@mui/icons-material/Share";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import BlogCard from "./BlogCard";
import NavBar from "./NavBar";

const BlogDetails = () => {
  const { id } = useParams();
  const [blog, setBlog] = useState(null);
  const [relatedBlogs, setRelatedBlogs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const navigate = useNavigate();
  const location = useLocation();
  const [showMoreCount, setShowMoreCount] = useState(2);
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));

  const handleBack = useCallback(() => {
    navigate("/", { replace: true });
  }, [navigate]);

  useEffect(() => {
    window.scrollTo({ top: 0, behavior: "smooth" });
  }, [id]);

  useEffect(() => {
    const getBlogDetails = async () => {
      try {
        const response = await fetchBlogs();
        const selectedBlog = response.data.find((b) => b._id === id);
        if (!selectedBlog) {
          throw new Error("Blog not found");
        }
        setBlog(selectedBlog);

        // Filter related blogs by category
        const filteredRelatedBlogs = response.data.filter(
          (b) => b.category === selectedBlog.category && b._id !== id
        );
        setRelatedBlogs(filteredRelatedBlogs);
      } catch (err) {
        setError(
          err.message || "Something went wrong while fetching blog details"
        );
      } finally {
        setLoading(false);
      }
    };

    getBlogDetails();
  }, [id]);

  useEffect(() => {
    const handlePopState = (event) => {
      if (location.pathname === `/blog/${id}`) {
        navigate("/", { replace: true });
      }
    };

    window.addEventListener("popstate", handlePopState);

    return () => {
      window.removeEventListener("popstate", handlePopState);
    };
  }, [id, navigate, location]);

  const shareBlogDetails = () => {
    const roomUrl = `${window.location.origin}/blog/${id}`;

    if (navigator.share) {
      navigator
        .share({
          title: blog.title,
          text: `Check out this amazing blog: ${blog.title}`,
          url: roomUrl,
        })
        .catch((error) => console.error("Error sharing:", error));
    } else {
      navigator.clipboard.writeText(roomUrl);
      alert("Blog link copied to clipboard!");
    }
  };

  const formatContent = (text) => {
    return text
      .replace(/\n/g, "<br>")
      .replace(/\*\*(.*?)\*\*/g, "<strong>$1</strong>")
      .replace(/\*(.*?)\*/g, "<em>$1</em>");
  };

  const handleShowMore = () => {
    setShowMoreCount((prevCount) => prevCount + 2);
  };

  if (loading) {
    return (
      <Container maxWidth="md" sx={{ textAlign: "center", marginTop: "40px" }}>
        <CircularProgress />
      </Container>
    );
  }

  if (error) {
    return (
      <Container maxWidth="md" sx={{ marginTop: "40px" }}>
        <Alert severity="error">{error}</Alert>
      </Container>
    );
  }

  return (
    <>
      <NavBar />
      <Container maxWidth="md" sx={{ marginTop: "40px", position: "relative" }}>
        <Box
          sx={{
            position: "absolute",
            left: { xs: "16px", md: "-80px" },
            top: "0",
          }}
        >
          <IconButton onClick={handleBack} aria-label="go back">
            <ArrowBackIcon />
          </IconButton>
        </Box>

        <Box
          sx={{
            position: "relative",
            width: "100%",
            height: "300px",
            marginBottom: "20px",
            marginTop: "100px",
          }}
        >
          <img
            src={blog.blog_img}
            alt={blog.title}
            style={{
              width: "100%",
              height: "100%",
              borderRadius: "8px",
              objectFit: "cover",
            }}
          />
          <IconButton
            onClick={shareBlogDetails}
            sx={{
              position: "absolute",
              top: "10px",
              right: "10px",
              backgroundColor: "rgba(0, 0, 0, 0.6)",
              color: "white",
              "&:hover": {
                backgroundColor: "rgba(0, 0, 0, 0.8)",
              },
            }}
          >
            <ShareIcon />
          </IconButton>
        </Box>

        <Typography variant="h3" color="primary" gutterBottom>
          {blog.title}
        </Typography>
        <Typography variant="subtitle1" color="text.secondary" gutterBottom>
          Category: {blog.category}
        </Typography>

        <Typography variant="body1" color="text.primary" paragraph>
          <span
            dangerouslySetInnerHTML={{ __html: formatContent(blog.content) }}
          />
        </Typography>

        <Typography variant="caption" color="text.secondary">
          Published on: {new Date(blog.createdAt).toLocaleDateString()}
        </Typography>

        <Box sx={{ mt: 4 }}>
          <Typography variant="h5" gutterBottom>
            Related Articles
          </Typography>
          <Grid container spacing={3}>
            {relatedBlogs.slice(0, showMoreCount).map((relatedBlog) => (
              <Grid item xs={12} sm={6} key={relatedBlog._id}>
                <BlogCard
                  title={relatedBlog.title}
                  description={relatedBlog.content}
                  image={relatedBlog.blog_img}
                  link={`/blog/${relatedBlog._id}`}
                  onClick={() => {
                    navigate(`/blog/${relatedBlog._id}`);
                    window.scrollTo({ top: 0, behavior: "smooth" });
                  }}
                />
              </Grid>
            ))}
          </Grid>
          {showMoreCount < relatedBlogs.length && (
            <Box sx={{ display: "flex", justifyContent: "center", mt: 3 }}>
              <Button
                variant="contained"
                onClick={handleShowMore}
                endIcon={<ExpandMoreIcon />}
                sx={{
                  width: isMobile ? "100%" : "auto",
                  py: isMobile ? 2 : 1,
                  fontSize: isMobile ? "1rem" : "inherit",
                }}
              >
                Show More
              </Button>
            </Box>
          )}
        </Box>
      </Container>
    </>
  );
};

export default BlogDetails;
